<template>
  <policy :permissions="['administrator']" :feature-flag="FEATURE_FLAGS.AI_CORE">
    <div class="business-data-tab">
      <!-- Help Banner for Low-tech Users -->
      <div 
        v-if="currentSubTab && subTabDescriptions[currentSubTab] && !dismissedBanners[currentSubTab]" 
        class="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-n-gray-7 dark:border-n-gray-5 flex gap-4 items-start shadow-sm"
      >
        <div class="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg shrink-0 text-blue-600 dark:text-blue-300">
          <fluent-icon icon="info" size="20" />
        </div>
        <div class="flex-1 text-sm">
          <h4 class="font-semibold text-slate-900 dark:text-white mb-1">
            {{ subTabDescriptions[currentSubTab].title }}
          </h4>
          <p class="text-slate-600 dark:text-slate-300 leading-relaxed">
            {{ subTabDescriptions[currentSubTab].desc }}
          </p>
        </div>
        <button
          class="p-1 hover:bg-blue-100 dark:hover:bg-blue-800/50 rounded-lg transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          @click="dismissBanner(currentSubTab)"
        >
          <fluent-icon icon="dismiss" size="18" />
        </button>
      </div>

      <!-- Sub Tab Content -->
      <div v-if="currentSubTab">
        <AiRulesTab
          v-if="currentSubTab === 'ai-rules'"
          :rules="aiRules"
          :pagination="aiRulePagination"
          :is-loading="isLoadingAiRules"
          :is-searching="isSearchingAiRules"
          :search-term="aiRuleFilters.search"
          :is-exporting="isExportingAiRulesXlsx"
          :is-importing="isImportingAiRules"
          :selected-ids="selectedAiRuleIds"
          :is-bulk-deleting="isBulkDeletingAiRules"
          :bulk-action="bulkAiRuleAction"
          :import-results="aiRuleImportResults"
          ref="aiRulesTab"
          @add-rule="openAiRuleModal"
          @edit-rule="editAiRule"
          @delete-rule="deleteAiRule"
          @search="onAiRuleSearch"
          @page-change="onAiRulePageChange"
          @export="onAiRuleExport"
          @import="onAiRuleImport"
          @toggle-select="onAiRuleToggleSelect"
          @toggle-select-page="onAiRuleToggleSelectPage"
          @clear-selection="onAiRuleClearSelection"
          @bulk-update-status="onAiRuleBulkUpdateStatus"
          @bulk-delete="onAiRuleBulkDelete"
          @toggle-status="onAiRuleToggleStatus"
        />

        <AiSampleSentencesTab
          v-else-if="currentSubTab === 'ai-sample-sentences'"
          :samples="aiSampleSentences"
          :pagination="aiSamplePagination"
          :is-loading="isLoadingAiSamples"
          :is-searching="isSearchingAiSamples"
          :search-term="aiSampleFilters.search"
          :sort="aiSampleFilters.sort"
          :is-exporting="isExportingAiSamplesXlsx"
          :is-importing="isImportingAiSamples"
          :selected-ids="selectedAiSampleIds"
          :is-bulk-deleting="isBulkDeletingAiSamples"
          :bulk-action="bulkAiSampleAction"
          :import-results="aiSampleImportResults"
          ref="aiSamplesTab"
          @add-sample="openAiSampleModal"
          @edit-sample="editAiSampleSentence"
          @delete-sample="deleteAiSampleSentence"
          @search="onAiSampleSearch"
          @sort-change="onAiSampleSortChange"
          @page-change="onAiSamplePageChange"
          @export="onAiSampleExport"
          @import="onAiSampleImport"
          @toggle-select="onAiSampleToggleSelect"
          @toggle-select-page="onAiSampleToggleSelectPage"
          @clear-selection="onAiSampleClearSelection"
          @bulk-update-status="onAiSampleBulkUpdateStatus"
          @bulk-delete="onAiSampleBulkDelete"
          @toggle-status="onAiSampleToggleStatus"
        />

        <FAQsTab
          v-else-if="currentSubTab === 'faqs' && canUseBusinessData"
          :faqs="faqs"
          :categories="faqCategoryOptions"
          :pagination="faqPagination"
          :is-loading="isLoading"
          :is-searching="isSearching"
          :is-exporting="isExportingFaqsXlsx"
          :is-importing="isImportingFaqs"
          :selected-ids="selectedFaqIds"
          :is-bulk-deleting="isBulkDeletingFaqs"
          :bulk-action="bulkFaqAction"
          :import-results="faqImportResults"
          ref="faqsTab"
          @add-faq="showFaqModal = true"
          @edit-faq="editFaq"
          @search="onFaqSearch"
          @filter-change="onFaqFilterChange"
          @page-change="onFaqPageChange"
          @toggle-select="onFaqToggleSelect"
          @toggle-select-page="onFaqToggleSelectPage"
          @clear-selection="onFaqClearSelection"
          @bulk-update-status="onFaqBulkUpdateStatus"
          @bulk-delete="onFaqBulkDelete"
          @toggle-status="onFaqToggleStatus"
          @delete-faq="deleteFaq"
          @export="onFaqExport"
          @import="onFaqImport"
        />

        <DataItemsTab
          v-else-if="currentSubTab === 'data-items' && canUseBusinessData"
          :data-items="dataItems"
          :categories="dataItemCategoryOptions"
          :web-sources="webContentSources"
          :pagination="itemPagination"
          :is-loading="isLoading"
          :is-searching="isSearching"
          :is-exporting="isExportingDataItemsXlsx"
          :is-importing="isImportingDataItems"
          :selected-ids="selectedDataItemIds"
          :is-bulk-deleting="isBulkDeletingDataItems"
          :bulk-action="bulkDataItemAction"
          :import-results="dataItemImportResults"
          ref="dataItemsTab"
          @add-data-item="showItemModal = true"
          @edit-data-item="editDataItem"
          @search="onItemSearch"
          @filter-change="onItemFilterChange"
          @page-change="onItemPageChange"
          @toggle-select="onDataItemToggleSelect"
          @toggle-select-page="onDataItemToggleSelectPage"
          @clear-selection="onDataItemClearSelection"
          @bulk-update-status="onDataItemBulkUpdateStatus"
          @bulk-delete="onDataItemBulkDelete"
          @toggle-status="onDataItemToggleStatus"
          @delete-data-item="deleteDataItem"
          @export="onDataItemExport"
          @import="onDataItemImport"
        />

        <ImageLibraryTab
          v-else-if="currentSubTab === 'image-library' && canUseImageLibrary"
        />

        <FileSourcesTab
          v-else-if="currentSubTab === 'file-sources' && canUseFileSources"
          :file-sources="fileSources"
          :pagination="fileSourcePagination"
          :is-loading="isLoadingFileSources"
          :is-searching="isSearchingFileSources"
          @add-file-source="openFileSourceModal"
          @delete-file-source="deleteFileSource"
          @search="onFileSourceSearch"
          @filter-change="onFileSourceFilterChange"
          @page-change="onFileSourcePageChange"
          @view-file-source="viewFileSourceDetails"
        />

        <WebSourcesTab
          v-else-if="currentSubTab === 'web-sources' && canUseWebImporter"
          :web-sources="webContentSources"
          :pagination="sourcePagination"
          :is-loading="isLoading"
          :is-searching="isSearching"
          @add-web-source="showSourceModal = true"
          @edit-web-source="editSource"
          @search="onSourceSearch"
          @filter-change="onSourceFilterChange"
          @page-change="onSourcePageChange"
          @refresh="loadData"
        />
      </div>

      <!-- Default State when no sub-tab is selected -->
      <div v-else class="flex items-center justify-center min-h-96 p-12">
        <div class="text-center max-w-md">
          <fluent-icon icon="database" size="48" class="text-slate-400 mx-auto mb-4" />
          <h3 class="text-xl font-medium text-slate-900 mb-2">{{ $t('BUSINESS_DATA.DEFAULT.TITLE') }}</h3>
          <p class="text-slate-600 mb-2 leading-relaxed">{{ $t('BUSINESS_DATA.DEFAULT.DESCRIPTION') }}</p>
          <p class="text-sm text-slate-500 italic">{{ $t('BUSINESS_DATA.DEFAULT.HINT') }}</p>
        </div>
      </div>

      <!-- Modals -->
      <FaqModal
        :show="showFaqModal"
        :faq="editingFaq"
        :categories="faqCategories"
        :business-categories="businessFaqCategories"
        @close="closeFaqModal"
        @saved="onFaqSaved"
        @category-created="onCategoryCreated"
        @category-updated="onCategoryUpdated"
      />

      <DataItemModal
        :show="showItemModal"
        :data-item="editingItem"
        :categories="itemCategories"
        :business-categories="businessDataCategories"
        @close="closeItemModal"
        @saved="onItemSaved"
        @category-created="onCategoryCreated"
        @category-updated="onCategoryUpdated"
      />

      <WebContentSourceModal
        :show="showSourceModal"
        :source="editingSource"
        @close="closeSourceModal"
        @saved="onSourceSaved"
      />

      <AiRuleModal
        :show="showAiRuleModal"
        :rule="editingAiRule"
        :loading="isSavingAiRule"
        @close="closeAiRuleModal"
        @save="onAiRuleSave"
      />

      <AiSampleSentenceModal
        :show="showAiSampleModal"
        :sample-sentence="editingAiSampleSentence"
        :loading="isSavingAiSample"
        @close="closeAiSampleModal"
        @save="onAiSampleSave"
      />

      <FileSourceModal
        :show="showFileSourceModal"
        @close="closeFileSourceModal"
        @saved="onFileSourceSaved"
      />

      <FileSourceDetailModal
        :show="showFileSourceDetailModal"
        :file-source="fileSourceDetail"
        :items="fileSourceDetailItems"
        :is-loading="isLoadingFileSourceDetail"
        :is-purging="isPurgingFileSource"
        :error="fileSourceDetailError"
        @close="closeFileSourceDetailModal"
        @purge="purgeFileSourceData"
      />
    </div>
  </policy>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import policy from 'dashboard/components/policy.vue';
import FAQsTab from './business-data/FAQsTab.vue';
import DataItemsTab from './business-data/DataItemsTab.vue';
import WebSourcesTab from './business-data/WebSourcesTab.vue';
import FileSourcesTab from './business-data/FileSourcesTab.vue';
import AiRulesTab from './business-data/AiRulesTab.vue';
import AiSampleSentencesTab from './business-data/AiSampleSentencesTab.vue';
import ImageLibraryTab from './ImageLibraryTab.vue';
import FaqModal from './FaqModal.vue';
import DataItemModal from './DataItemModal.vue';
import WebContentSourceModal from './WebContentSourceModal.vue';
import AiRuleModal from './AiRuleModal.vue';
import AiSampleSentenceModal from './AiSampleSentenceModal.vue';
import FileSourceModal from './FileSourceModal.vue';
import FileSourceDetailModal from './FileSourceDetailModal.vue';

export default {
  name: 'BusinessDataTab',

  components: {
    policy,
    FAQsTab,
    DataItemsTab,
    WebSourcesTab,
    AiRulesTab,
    AiSampleSentencesTab,
    ImageLibraryTab,
    FaqModal,
    DataItemModal,
    WebContentSourceModal,
    AiRuleModal,
    AiSampleSentenceModal,
    FileSourcesTab,
    FileSourceModal,
    FileSourceDetailModal,
  },

  props: {
    activeTab: { type: String, default: 'business-data' },
    canUseBusinessData: { type: Boolean, default: false },
    canUseFileSources: { type: Boolean, default: false },
    canUseWebImporter: { type: Boolean, default: false },
    canUseImageLibrary: { type: Boolean, default: false },
  },

  data() {
    return {
      FEATURE_FLAGS,
      currentSubTab: null,
      dismissedBanners: this.loadDismissedBanners(),
      faqs: [],
      dataItems: [],
      aiRules: [],
      aiSampleSentences: [],
      faqCategories: [],
      itemCategories: [],
      businessFaqCategories: [],
      businessDataCategories: [],
      webContentSources: [],
      isLoading: false,
      isSearching: false,
      isLoadingAiRules: false,
      isSearchingAiRules: false,
      isLoadingAiSamples: false,
      isSearchingAiSamples: false,
      fileSources: [],
      fileSourcePagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
      },
      isLoadingFileSources: false,
      isSearchingFileSources: false,
      isExportingAiRulesXlsx: false,
      isImportingAiRules: false,
      isExportingAiSamplesXlsx: false,
      isImportingAiSamples: false,
      isExportingFaqsXlsx: false,
      isImportingFaqs: false,
      faqImportResults: null,
      aiSampleImportResults: null,
      aiRuleImportResults: null,
      dataItemImportResults: null,
      isExportingDataItemsXlsx: false,
      isImportingDataItems: false,
      fileSourceFilters: {
        search: '',
        status: '',
      },
      selectedFaqIds: [],
      selectedAiRuleIds: [],
      selectedAiSampleIds: [],
      selectedDataItemIds: [],
      isBulkDeletingFaqs: false,
      isBulkDeletingAiRules: false,
      isBulkDeletingAiSamples: false,
      isBulkDeletingDataItems: false,
      bulkFaqAction: '',
      bulkAiRuleAction: '',
      bulkAiSampleAction: '',
      bulkDataItemAction: '',
      showFaqModal: false,
      showItemModal: false,
      showSourceModal: false,
      showAiRuleModal: false,
      showAiSampleModal: false,
      showFileSourceModal: false,
      showFileSourceDetailModal: false,
      fileSourceDetail: null,
      fileSourceDetailItems: [],
      isLoadingFileSourceDetail: false,
      isPurgingFileSource: false,
      fileSourceDetailError: '',
      editingFaq: null,
      editingItem: null,
      editingSource: null,
      editingAiRule: null,
      editingAiSampleSentence: null,
      isSavingAiRule: false,
      isSavingAiSample: false,
      faqPagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
      },
      itemPagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
      },
      sourcePagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
      },
      aiRulePagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
      },
      aiSamplePagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
      },
      faqFilters: {
        search: '',
        category: '',
        status: '',
        sort: 'created_at_desc',
      },
      itemFilters: {
        search: '',
        category: '',
        status: '',
        web_content_source_id: '',
        sort: 'created_at_desc',
      },
      sourceFilters: {
        search: '',
        status: '',
        type: '',
      },
      aiRuleFilters: {
        search: '',
      },
      aiSampleFilters: {
        search: '',
        sort: 'created_at_desc',
      },
      subTabDescriptions: {
        'ai-rules': {
          title: 'Quy tắc ứng xử (AI Rules)',
          desc: 'Quy định cách Bot hành xử. Ví dụ: "Luôn xưng hô là em", "Không được nhắc đến đối thủ cạnh tranh", "Chỉ trả lời ngắn gọn dưới 3 câu".'
        },
        'ai-sample-sentences': {
          title: 'Huấn luyện câu mẫu (Sample Sentences)',
          desc: 'Dạy Bot cách trả lời chính xác cho các câu hỏi khó. Ví dụ: Nếu khách hỏi "Giá sao đắt thế?", hãy dạy Bot trả lời khéo léo về giá trị sản phẩm.'
        },
        'faqs': {
          title: 'Câu hỏi thường gặp (FAQs)',
          desc: 'Danh sách các câu hỏi phổ biến. Bot sẽ dùng dữ liệu này để trả lời ngay lập tức các vấn đề cơ bản như: Giờ làm việc, Địa chỉ, Hotline.'
        },
        'data-items': {
          title: 'Dữ liệu doanh nghiệp (Business Data)',
          desc: 'Nơi chứa toàn bộ kiến thức về sản phẩm, dịch vụ, chính sách bảo hành... Đây là "bộ não" chính giúp Bot tư vấn bán hàng.'
        },
        'file-sources': {
          title: 'Nguồn dữ liệu từ File',
          desc: 'Tải lên các tài liệu PDF, Word, TXT có sẵn của bạn. Bot sẽ tự động đọc và học nội dung bên trong.'
        },
        'web-sources': {
          title: 'Nguồn dữ liệu từ Website',
          desc: 'Nhập đường link website của bạn. Bot sẽ tự động "quét" và học thông tin sản phẩm từ trang web.'
        }
      }
    };
  },

  computed: {
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },

    faqCategoryOptions() {
      if (this.businessFaqCategories.length) {
        return this.businessFaqCategories;
      }
      return this.faqCategories;
    },

    dataItemCategoryOptions() {
      if (this.businessDataCategories.length) {
        return this.businessDataCategories;
      }
      return this.itemCategories;
    },
  },

  watch: {
    activeTab: {
      handler() {
        this.updateCurrentSubTab();
      },
      immediate: true,
    },
    currentAccountId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadAllData();
      }
    },
    canUseBusinessData() {
      this.ensureValidSubTab();
    },
    canUseFileSources(newVal) {
      this.ensureValidSubTab();
      if (newVal) {
        this.loadFileSources();
        return;
      }
      if (!newVal) {
        this.fileSources = [];
        this.fileSourcePagination = {
          current_page: 1,
          per_page: 20,
          total_count: 0,
          total_pages: 0,
          has_next_page: false,
          has_prev_page: false,
        };
        this.showFileSourceModal = false;
        this.showFileSourceDetailModal = false;
        this.fileSourceDetail = null;
        this.fileSourceDetailItems = [];
        this.isLoadingFileSources = false;
      }
    },
    canUseWebImporter() {
      this.ensureValidSubTab();
    },
    canUseImageLibrary() {
      this.ensureValidSubTab();
    },
  },

  mounted() {
    this.updateCurrentSubTab();
    this.loadAllData();
  },

  methods: {
    loadDismissedBanners() {
      try {
        const stored = localStorage.getItem('ai_business_data_dismissed_banners');
        return stored ? JSON.parse(stored) : {};
      } catch (e) {
        return {};
      }
    },
    
    dismissBanner(tabKey) {
      this.dismissedBanners[tabKey] = true;
      try {
        localStorage.setItem('ai_business_data_dismissed_banners', JSON.stringify(this.dismissedBanners));
      } catch (e) {
        console.warn('Failed to save dismissed banner state:', e);
      }
    },
    
    availableSubTabs() {
      const tabs = ['ai-rules', 'ai-sample-sentences'];
      if (this.canUseFileSources) {
        tabs.push('file-sources');
      }
      if (this.canUseBusinessData) {
        tabs.push('faqs', 'data-items');
      }
      if (this.canUseImageLibrary) {
        tabs.push('image-library');
      }
      if (this.canUseWebImporter) {
        tabs.push('web-sources');
      }
      return tabs;
    },

    async loadData() {
      await this.loadAllData();
    },

    ensureValidSubTab() {
      const available = this.availableSubTabs();
      if (!available.includes(this.currentSubTab)) {
        [this.currentSubTab] = available;
      }
      if (!this.currentSubTab && available.length) {
        [this.currentSubTab] = available;
      }
    },

    updateCurrentSubTab() {
      if (this.activeTab && this.activeTab.startsWith('business-data:')) {
        const parts = this.activeTab.split(':');
        this.currentSubTab = parts[1] || 'ai-rules';
      } else if (this.activeTab === 'business-data') {
        this.currentSubTab = 'ai-rules';
      } else {
        this.currentSubTab = null;
      }
      this.ensureValidSubTab();
    },

    async loadAllData() {
      if (!this.currentAccountId) {
        this.isLoading = false;
        this.isLoadingAiRules = false;
        this.isLoadingAiSamples = false;
        return;
      }
      const loaders = [
        this.loadAiRules(),
        this.loadAiSampleSentences(),
      ];
      if (this.canUseBusinessData) {
        loaders.push(this.loadFaqs(), this.loadDataItems(), this.loadBusinessCategories());
      } else {
        this.faqs = [];
        this.dataItems = [];
      }
      if (this.canUseWebImporter) {
        loaders.push(this.loadWebContentSources());
      } else {
        this.webContentSources = [];
      }

      if (this.canUseFileSources) {
        loaders.push(this.loadFileSources());
      } else {
        this.fileSources = [];
      }

      this.isLoading = this.canUseBusinessData || this.canUseWebImporter || this.canUseFileSources;
      try {
        await Promise.allSettled(loaders);
      } catch (error) {
        console.error('Failed to load AI data resources:', error);
        useAlert(this.$t('BUSINESS_DATA.ERROR.LOAD_FAILED'), { type: 'error' });
      } finally {
        this.isLoading = false;
      }
    },

    async loadAiRules() {
      if (!this.currentAccountId) return;
      this.isLoadingAiRules = true;
      try {
        const params = {
          page: this.aiRulePagination.current_page,
          per_page: this.aiRulePagination.per_page,
        };
        if (this.aiRuleFilters.search) {
          params.search = this.aiRuleFilters.search;
        }
        const { data } = await window.axios.get(
          `/api/v1/accounts/${this.currentAccountId}/ai_rules`,
          { params }
        );
        this.aiRules = data.ai_rules || [];
        const meta = data.meta || {};
        this.aiRulePagination = {
          current_page: meta.current_page || 1,
          per_page: meta.per_page || this.aiRulePagination.per_page,
          total_count: meta.total_count || this.aiRules.length,
          total_pages: meta.total_pages || 1,
          has_next_page: meta.has_next_page || false,
          has_prev_page: meta.has_prev_page || false,
        };
        const currentIds = this.aiRules.map(rule => rule.id);
        this.selectedAiRuleIds = this.selectedAiRuleIds.filter(id => currentIds.includes(id));
      } catch (error) {
        console.error('Error loading AI rules:', error);
        useAlert(this.$t('BUSINESS_DATA.AI_RULES.LOAD_FAILED'), {
          type: 'error',
        });
      } finally {
        this.isLoadingAiRules = false;
        this.isSearchingAiRules = false;
      }
    },

    async loadAiSampleSentences() {
      if (!this.currentAccountId) return;
      this.isLoadingAiSamples = true;
      try {
        const params = {
          page: this.aiSamplePagination.current_page,
          per_page: this.aiSamplePagination.per_page,
        };
        if (this.aiSampleFilters.search) {
          params.search = this.aiSampleFilters.search;
        }
        if (this.aiSampleFilters.sort) {
          params.sort = this.aiSampleFilters.sort;
        }
        const { data } = await window.axios.get(
          `/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences`,
          { params }
        );
        this.aiSampleSentences = data.ai_sample_sentences || [];
        const meta = data.meta || {};
        this.aiSamplePagination = {
          current_page: meta.current_page || 1,
          per_page: meta.per_page || this.aiSamplePagination.per_page,
          total_count: meta.total_count || this.aiSampleSentences.length,
          total_pages: meta.total_pages || 1,
          has_next_page: meta.has_next_page || false,
          has_prev_page: meta.has_prev_page || false,
        };
        const currentIds = this.aiSampleSentences.map(sample => sample.id);
        this.selectedAiSampleIds = this.selectedAiSampleIds.filter(id => currentIds.includes(id));
      } catch (error) {
        console.error('Error loading AI sample sentences:', error);
        useAlert(this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.LOAD_FAILED'), {
          type: 'error',
        });
      } finally {
        this.isLoadingAiSamples = false;
        this.isSearchingAiSamples = false;
      }
    },

    async onAiRuleExport() {
      if (!this.currentAccountId) return;
      this.isExportingAiRulesXlsx = true;
      try {
        const params = new URLSearchParams();
        if (this.aiRuleFilters.search) params.append('search', this.aiRuleFilters.search);
        const query = params.toString();
        const url = `/api/v1/accounts/${this.currentAccountId}/ai_rules/export${query ? `?${query}` : ''}`;
        const response = await window.axios.get(url, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.setAttribute('download', `ai_rules_${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Failed to export AI rules', error);
        useAlert(this.$t('BUSINESS_DATA.AI_RULES.EXPORT_ERROR'), {
          type: 'error',
        });
      } finally {
        this.isExportingAiRulesXlsx = false;
      }
    },

    async onAiRuleImport(file) {
      if (!this.currentAccountId || !file) return;
      this.isImportingAiRules = true;
      try {
        const form = new FormData();
        form.append('import_file', file);
        const { data } = await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/ai_rules/import`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        this.aiRuleImportResults = data || {};
        const { created = 0, updated = 0, failed_count: failed = 0 } = this.aiRuleImportResults;
        const message = this.$t('BUSINESS_DATA.AI_RULES.IMPORT_RESULT', { created, updated, failed });
        useAlert(message, {
          type: failed > 0 ? 'alert' : 'success',
          persist: true,
          action: {
            type: 'button',
            message: this.$t('GENERAL.VIEW_DETAILS'),
            onClick: () => {
              this.$refs.aiRulesTab?.showImportResults();
            },
          },
        });
        await this.loadAiRules();
      } catch (error) {
        console.error('Failed to import AI rules', error);
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.AI_RULES.IMPORT_ERROR');
        useAlert(message, { type: 'error' });
      } finally {
        this.isImportingAiRules = false;
      }
    },

    onAiRuleToggleSelect({ id, checked }) {
      if (!id) return;
      if (checked) {
        if (!this.selectedAiRuleIds.includes(id)) {
          this.selectedAiRuleIds = [...this.selectedAiRuleIds, id];
        }
      } else {
        this.selectedAiRuleIds = this.selectedAiRuleIds.filter(existingId => existingId !== id);
      }
    },

    onAiRuleToggleSelectPage({ ids, selectAll }) {
      const uniqueIds = Array.from(new Set(ids || []));
      if (selectAll) {
        const combined = new Set([...this.selectedAiRuleIds, ...uniqueIds]);
        this.selectedAiRuleIds = Array.from(combined);
      } else {
        this.selectedAiRuleIds = this.selectedAiRuleIds.filter(id => !uniqueIds.includes(id));
      }
    },

    onAiRuleClearSelection() {
      this.selectedAiRuleIds = [];
    },

    async onAiRuleBulkUpdateStatus(isActive) {
      if (!this.currentAccountId || !this.selectedAiRuleIds.length) return;
      
      // If activating, check the limit of 5 active rules
      if (isActive) {
        const currentActiveCount = (this.aiRules || []).filter(rule => rule?.is_active).length;
        const selectedRules = this.aiRules.filter(rule => this.selectedAiRuleIds.includes(rule.id));
        const inactiveSelectedCount = selectedRules.filter(rule => !rule.is_active).length;
        const maxCanActivate = Math.max(0, 5 - currentActiveCount);
        
        if (maxCanActivate === 0) {
          useAlert(
            this.$t('BUSINESS_DATA.AI_RULES.ACTIVATE_LIMIT_TOOLTIP') ||
              'Maximum 5 active rules allowed',
            { type: 'error' }
          );
          return;
        }
        
        if (inactiveSelectedCount > maxCanActivate) {
          useAlert(
            this.$t('BUSINESS_DATA.AI_RULES.ACTIVATE_LIMIT_TOOLTIP') || 
            `Only ${maxCanActivate} more rule(s) can be activated. Please select fewer rules.`,
            'error'
          );
          return;
        }
      }
      
      this.bulkAiRuleAction = isActive ? 'activate' : 'deactivate';
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/ai_rules/bulk_update_status`, {
          ids: this.selectedAiRuleIds,
          is_active: isActive,
        });
        useAlert(
          this.$t(isActive ? 'BUSINESS_DATA.AI_RULES.BULK_ACTIVATE_SUCCESS' : 'BUSINESS_DATA.AI_RULES.BULK_DEACTIVATE_SUCCESS', {
            count: this.selectedAiRuleIds.length,
          }),
          'success'
        );
        this.onAiRuleClearSelection();
        await this.loadAiRules();
      } catch (error) {
        console.error('Failed to bulk update AI rules status', error);
        useAlert(this.$t('BUSINESS_DATA.ERROR.UPDATE_FAILED'), { type: 'error' });
      } finally {
        this.bulkAiRuleAction = '';
      }
    },

    async onAiRuleBulkDelete() {
      if (!this.currentAccountId || !this.selectedAiRuleIds.length) return;
      const confirmMessage = this.$t('BUSINESS_DATA.GENERAL.BULK_DELETE_CONFIRM', { count: this.selectedAiRuleIds.length });
      if (!window.confirm(confirmMessage)) {
        return;
      }
      this.isBulkDeletingAiRules = true;
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/ai_rules/bulk_delete`, {
          ids: this.selectedAiRuleIds,
        });
        useAlert(
          this.$t('BUSINESS_DATA.AI_RULES.BULK_DELETE_SUCCESS', {
            count: this.selectedAiRuleIds.length,
          }),
          { type: 'success' }
        );
        this.onAiRuleClearSelection();
        await this.loadAiRules();
      } catch (error) {
        console.error('Failed to bulk delete AI rules', error);
        useAlert(this.$t('BUSINESS_DATA.ERROR.DELETE_FAILED'), { type: 'error' });
      } finally {
        this.isBulkDeletingAiRules = false;
      }
    },

    async onAiRuleToggleStatus(rule) {
      if (!this.currentAccountId || !rule) return;
      try {
        const newStatus = !rule.is_active;
        await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/ai_rules/${rule.id}`, {
          ai_rule: { is_active: newStatus },
        });
        useAlert(
          this.$t(newStatus ? 'BUSINESS_DATA.AI_RULES.ACTIVATED_SUCCESS' : 'BUSINESS_DATA.AI_RULES.DEACTIVATED_SUCCESS'),
          { type: 'success' }
        );
        await this.loadAiRules();
      } catch (error) {
        console.error('Failed to toggle AI rule status', error);
        useAlert(this.$t('BUSINESS_DATA.ERROR.UPDATE_FAILED'), { type: 'error' });
      }
    },

    async onAiSampleExport() {
      if (!this.currentAccountId) return;
      this.isExportingAiSamplesXlsx = true;
      try {
        const params = new URLSearchParams();
        if (this.aiSampleFilters.search) params.append('search', this.aiSampleFilters.search);
        if (this.aiSampleFilters.sort) params.append('sort', this.aiSampleFilters.sort);
        const query = params.toString();
        const url = `/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences/export${query ? `?${query}` : ''}`;
        const response = await window.axios.get(url, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.setAttribute('download', `ai_sample_sentences_${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Failed to export AI sample sentences', error);
        useAlert(this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.EXPORT_ERROR'), {
          type: 'error',
        });
      } finally {
        this.isExportingAiSamplesXlsx = false;
      }
    },

    async onAiSampleImport(file) {
      if (!this.currentAccountId || !file) return;
      this.isImportingAiSamples = true;
      try {
        const form = new FormData();
        form.append('import_file', file);
        const { data } = await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences/import`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        this.aiSampleImportResults = data || {};
        const { created = 0, updated = 0, failed_count: failed = 0 } = this.aiSampleImportResults;
        const message = this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.IMPORT_RESULT', { created, updated, failed });
        useAlert(message, {
          type: failed > 0 ? 'alert' : 'success',
          persist: true,
          action: {
            type: 'button',
            message: this.$t('GENERAL.VIEW_DETAILS'),
            onClick: () => {
              this.$refs.aiSamplesTab?.showImportResults();
            },
          },
        });
        await this.loadAiSampleSentences();
      } catch (error) {
        console.error('Failed to import AI sample sentences', error);
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.IMPORT_ERROR');
        useAlert(message, { type: 'error' });
      } finally {
        this.isImportingAiSamples = false;
      }
    },

    async loadFileSources() {
      if (!this.currentAccountId || !this.canUseFileSources) {
        this.isLoadingFileSources = false;
        return;
      }
      this.isLoadingFileSources = true;
      try {
        const params = {
          page: this.fileSourcePagination.current_page,
          per_page: this.fileSourcePagination.per_page,
        };
        if (this.fileSourceFilters.search) {
          params.search = this.fileSourceFilters.search;
        }
        if (this.fileSourceFilters.status) {
          params.status = this.fileSourceFilters.status;
        }
        const { data } = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/file_sources`, { params });
        this.fileSources = data.file_sources || [];
        const meta = data.pagination || {};
        this.fileSourcePagination = {
          current_page: meta.current_page || 1,
          per_page: meta.per_page || this.fileSourcePagination.per_page,
          total_count: meta.total_count || this.fileSources.length,
          total_pages: meta.total_pages || 1,
          has_next_page: meta.has_next_page || false,
          has_prev_page: meta.has_prev_page || false,
        };
      } catch (error) {
        console.error('Failed to load file sources:', error);
        useAlert(this.$t('BUSINESS_DATA.FILE_SOURCES.ERRORS.LOAD_FAILED'), {
          type: 'error',
        });
      } finally {
        this.isLoadingFileSources = false;
        this.isSearchingFileSources = false;
      }
    },

    onAiSampleToggleSelect({ id, checked }) {
      if (!id) return;
      if (checked) {
        if (!this.selectedAiSampleIds.includes(id)) {
          this.selectedAiSampleIds = [...this.selectedAiSampleIds, id];
        }
      } else {
        this.selectedAiSampleIds = this.selectedAiSampleIds.filter(existingId => existingId !== id);
      }
    },

    onAiSampleToggleSelectPage({ ids, selectAll }) {
      const uniqueIds = Array.from(new Set(ids || []));
      if (selectAll) {
        const combined = new Set([...this.selectedAiSampleIds, ...uniqueIds]);
        this.selectedAiSampleIds = Array.from(combined);
      } else {
        this.selectedAiSampleIds = this.selectedAiSampleIds.filter(id => !uniqueIds.includes(id));
      }
    },

    onAiSampleClearSelection() {
      this.selectedAiSampleIds = [];
    },

    async onAiSampleBulkUpdateStatus(isActive) {
      if (!this.currentAccountId || !this.selectedAiSampleIds.length) return;
      this.bulkAiSampleAction = isActive ? 'activate' : 'deactivate';
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences/bulk_update_status`, {
          ids: this.selectedAiSampleIds,
          is_active: isActive,
        });
        useAlert(
          this.$t(isActive ? 'BUSINESS_DATA.AI_SAMPLE_SENTENCES.BULK_ACTIVATE_SUCCESS' : 'BUSINESS_DATA.AI_SAMPLE_SENTENCES.BULK_DEACTIVATE_SUCCESS', {
            count: this.selectedAiSampleIds.length,
          }),
          'success'
        );
        this.onAiSampleClearSelection();
        await this.loadAiSampleSentences();
      } catch (error) {
        console.error('Failed to bulk update AI sample sentences status', error);
        useAlert(this.$t('BUSINESS_DATA.ERROR.UPDATE_FAILED'), { type: 'error' });
      } finally {
        this.bulkAiSampleAction = '';
      }
    },

    async onAiSampleBulkDelete() {
      if (!this.currentAccountId || !this.selectedAiSampleIds.length) return;
      const confirmMessage = this.$t('BUSINESS_DATA.GENERAL.BULK_DELETE_CONFIRM', { count: this.selectedAiSampleIds.length });
      if (!window.confirm(confirmMessage)) {
        return;
      }
      this.isBulkDeletingAiSamples = true;
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences/bulk_delete`, {
          ids: this.selectedAiSampleIds,
        });
        useAlert(
          this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.BULK_DELETE_SUCCESS', {
            count: this.selectedAiSampleIds.length,
          }),
          { type: 'success' }
        );
        this.onAiSampleClearSelection();
        await this.loadAiSampleSentences();
      } catch (error) {
        console.error('Failed to bulk delete AI sample sentences', error);
        useAlert(this.$t('BUSINESS_DATA.ERROR.DELETE_FAILED'), { type: 'error' });
      } finally {
        this.isBulkDeletingAiSamples = false;
      }
    },

    async onAiSampleToggleStatus(sample) {
      if (!this.currentAccountId || !sample) return;
      try {
        const newStatus = !sample.is_active;
        await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences/${sample.id}`, {
          ai_sample_sentence: { is_active: newStatus },
        });
        useAlert(
          this.$t(newStatus ? 'BUSINESS_DATA.AI_SAMPLE_SENTENCES.ACTIVATED_SUCCESS' : 'BUSINESS_DATA.AI_SAMPLE_SENTENCES.DEACTIVATED_SUCCESS'),
          'success'
        );
        await this.loadAiSampleSentences();
      } catch (error) {
        console.error('Failed to toggle AI sample sentence status', error);
        useAlert(this.$t('BUSINESS_DATA.ERROR.UPDATE_FAILED'), { type: 'error' });
      }
    },

    onFileSourceSearch(query) {
      if (!this.canUseFileSources) return;
      this.fileSourceFilters.search = query;
      this.fileSourcePagination.current_page = 1;
      this.isSearchingFileSources = true;
      this.loadFileSources();
    },

    onFileSourceFilterChange(filters) {
      if (!this.canUseFileSources) return;
      this.fileSourceFilters.status = filters?.status || '';
      this.fileSourcePagination.current_page = 1;
      this.loadFileSources();
    },

    onFileSourcePageChange(page) {
      if (!this.canUseFileSources) return;
      this.fileSourcePagination.current_page = page;
      this.loadFileSources();
    },

    openFileSourceModal() {
      if (!this.canUseFileSources) return;
      this.showFileSourceModal = true;
    },

    closeFileSourceModal() {
      this.showFileSourceModal = false;
    },

    async onFileSourceSaved() {
      if (!this.canUseFileSources) return;
      this.closeFileSourceModal();
      await this.loadFileSources();
    },

    async deleteFileSource(src) {
      if (!this.currentAccountId || !src || !this.canUseFileSources) return;
      const confirmMessage = this.$t('BUSINESS_DATA.FILE_SOURCES.CONFIRM_DELETE');
      if (!window.confirm(confirmMessage)) {
        return;
      }
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/file_sources/${src.id}`);
        useAlert(this.$t('BUSINESS_DATA.FILE_SOURCES.DELETED_SUCCESS'), {
          type: 'success',
        });
        await this.loadFileSources();
        if (this.fileSourceDetail && this.fileSourceDetail.id === src.id) {
          this.closeFileSourceDetailModal();
        }
      } catch (error) {
        console.error('Failed to delete file source', error);
        useAlert(this.$t('BUSINESS_DATA.FILE_SOURCES.ERRORS.DELETE_FAILED'), {
          type: 'error',
        });
      }
    },

    async viewFileSourceDetails(src) {
      if (!this.currentAccountId || !src || !this.canUseFileSources) return;
      this.fileSourceDetail = src;
      this.fileSourceDetailItems = [];
      this.fileSourceDetailError = '';
      this.isLoadingFileSourceDetail = true;
      this.showFileSourceDetailModal = true;
      try {
        const { data } = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/file_sources/${src.id}`);
        this.fileSourceDetail = data.file_source || src;
        this.fileSourceDetailItems = (data.file_source?.items || []).map(item => ({
          ...item,
          created_at: item.created_at,
        }));
        if (this.fileSourceDetail) {
          this.fileSourceDetail.items_count = this.fileSourceDetailItems.length;
        }
      } catch (error) {
        console.error('Failed to load file source detail', error);
        this.fileSourceDetailError = this.$t('BUSINESS_DATA.FILE_SOURCES.ERRORS.LOAD_FAILED');
      } finally {
        this.isLoadingFileSourceDetail = false;
      }
    },

    closeFileSourceDetailModal() {
      this.showFileSourceDetailModal = false;
      this.fileSourceDetail = null;
      this.fileSourceDetailItems = [];
      this.fileSourceDetailError = '';
    },

    async purgeFileSourceData() {
      if (!this.currentAccountId || !this.fileSourceDetail) return;
      const confirmMessage = this.$t('BUSINESS_DATA.FILE_SOURCES.CONFIRM_PURGE');
      if (!window.confirm(confirmMessage)) {
        return;
      }
      this.isPurgingFileSource = true;
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/file_sources/${this.fileSourceDetail.id}/purge_data`);
        useAlert(this.$t('BUSINESS_DATA.FILE_SOURCES.PURGE_SUCCESS'), {
          type: 'success',
        });
        await this.loadFileSources();
        await this.viewFileSourceDetails(this.fileSourceDetail);
      } catch (error) {
        console.error('Failed to purge file source data', error);
        useAlert(this.$t('BUSINESS_DATA.FILE_SOURCES.ERRORS.PURGE_FAILED'), {
          type: 'error',
        });
      } finally {
        this.isPurgingFileSource = false;
      }
    },

    async loadFaqs() {
      try {
        const params = {
          page: this.faqPagination.current_page,
          per_page: this.faqPagination.per_page,
          ...this.faqFilters
        };
        
        const response = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/business_faqs`, { params });
        this.faqs = response.data.faqs || [];
        this.faqCategories = response.data.meta?.categories || [];
        const currentIds = this.faqs.map(faq => faq.id);
        this.selectedFaqIds = this.selectedFaqIds.filter(id => currentIds.includes(id));
        this.faqPagination = {
          current_page: response.data.meta.current_page,
          per_page: response.data.meta.per_page,
          total_count: response.data.meta.total_count,
          total_pages: response.data.meta.total_pages,
          has_next_page: response.data.meta.has_next_page,
          has_prev_page: response.data.meta.has_prev_page
        };
      } catch (error) {
        console.error('Error loading FAQs:', error);
      }
    },
    
    async loadDataItems() {
      try {
        const params = {
          page: this.itemPagination.current_page,
          per_page: this.itemPagination.per_page,
          ...this.itemFilters
        };
        
        const response = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/business_data_items`, { params });
        this.dataItems = response.data.data_items || [];
        this.itemCategories = response.data.meta?.categories || [];
        this.itemPagination = {
          current_page: response.data.meta.current_page,
          per_page: response.data.meta.per_page,
          total_count: response.data.meta.total_count,
          total_pages: response.data.meta.total_pages,
          has_next_page: response.data.meta.has_next_page,
          has_prev_page: response.data.meta.has_prev_page
        };
        const currentIds = this.dataItems.map(item => item.id);
        this.selectedDataItemIds = this.selectedDataItemIds.filter(id => currentIds.includes(id));
      } catch (error) {
        console.error('Error loading data items:', error);
      }
    },
    
    async loadBusinessCategories() {
      try {
        const response = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/business_categories`);
        const categories = response.data.business_categories || [];
        
        this.businessFaqCategories = categories.filter(cat => cat.type === 'faq');
        this.businessDataCategories = categories.filter(cat => cat.type === 'data');
      } catch (error) {
        console.error('Error loading business categories:', error);
        // Set fallback data for testing
        this.businessFaqCategories = [
          { id: 1, name: 'General FAQ', type: 'faq' },
          { id: 2, name: 'Technical Support', type: 'faq' }
        ];
        this.businessDataCategories = [
          { id: 1, name: 'Products', type: 'data' },
          { id: 2, name: 'Services', type: 'data' },
          { id: 3, name: 'Pricing', type: 'data' }
        ];
      }
    },
    
    async loadWebContentSources() {
      try {
        const params = {
          page: this.sourcePagination.current_page,
          per_page: this.sourcePagination.per_page,
          ...this.sourceFilters
        };
        
        const response = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/web_content_sources`, { params });
        this.webContentSources = response.data.web_content_sources || [];
        this.sourcePagination = {
          current_page: response.data.pagination.current_page,
          per_page: response.data.pagination.per_page,
          total_count: response.data.pagination.total_count,
          total_pages: response.data.pagination.total_pages,
          has_next_page: response.data.pagination.has_next_page,
          has_prev_page: response.data.pagination.has_prev_page
        };
      } catch (error) {
        console.error('Error loading web content sources:', error);
      }
    },
    
    // FAQ methods
    onFaqSearch(searchQuery) {
      this.faqFilters.search = searchQuery;
      this.faqPagination.current_page = 1;
      this.loadFaqs();
    },
    
    onFaqFilterChange(filters) {
      this.faqFilters.category = filters.category;
      this.faqFilters.status = filters.status;
      this.faqFilters.sort = filters.sort || 'created_at_desc';
      this.faqPagination.current_page = 1;
      this.loadFaqs();
    },
    
    onFaqPageChange(page) {
      this.faqPagination.current_page = page;
      this.loadFaqs();
    },
    
    editFaq(faq) {
      this.editingFaq = { ...faq };
      this.showFaqModal = true;
    },
    
    closeFaqModal() {
      this.showFaqModal = false;
      this.editingFaq = null;
    },
    
    async onFaqSaved() {
      const wasEditing = !!this.editingFaq;
      this.closeFaqModal();
      await this.loadFaqs();
      this.updateSubTabCounts();
      const message = wasEditing 
        ? this.$t('BUSINESS_DATA.FAQ.UPDATED_SUCCESS')
        : this.$t('BUSINESS_DATA.FAQ.ADDED_SUCCESS');
      useAlert(message, { type: 'success' });
    },

    onFaqToggleSelect({ id, checked }) {
      if (!id) return;
      if (checked) {
        if (!this.selectedFaqIds.includes(id)) {
          this.selectedFaqIds = [...this.selectedFaqIds, id];
        }
      } else {
        this.selectedFaqIds = this.selectedFaqIds.filter(existingId => existingId !== id);
      }
    },

    onFaqToggleSelectPage({ ids, selectAll }) {
      const uniqueIds = Array.from(new Set(ids || []));
      if (!uniqueIds.length) return;
      if (selectAll) {
        const combined = new Set([...this.selectedFaqIds, ...uniqueIds]);
        this.selectedFaqIds = Array.from(combined);
      } else {
        this.selectedFaqIds = this.selectedFaqIds.filter(id => !uniqueIds.includes(id));
      }
    },

    onFaqClearSelection() {
      this.selectedFaqIds = [];
    },

    async onFaqBulkUpdateStatus(isActive) {
      if (!this.currentAccountId || !this.selectedFaqIds.length) return;
      this.bulkFaqAction = isActive ? 'activate' : 'deactivate';
      const count = this.selectedFaqIds.length;
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/business_faqs/bulk_update_status`, {
          ids: this.selectedFaqIds,
          is_active: isActive,
        });
        const successKey = isActive
          ? 'BUSINESS_DATA.FAQS.BULK_ACTIVATE_SUCCESS'
          : 'BUSINESS_DATA.FAQS.BULK_DEACTIVATE_SUCCESS';
        useAlert(this.$t(successKey, { count }), { type: 'success' });
        this.onFaqClearSelection();
        await this.loadFaqs();
        this.updateSubTabCounts();
      } catch (error) {
        const errorKey = isActive
          ? 'BUSINESS_DATA.FAQS.BULK_ACTIVATE_ERROR'
          : 'BUSINESS_DATA.FAQS.BULK_DEACTIVATE_ERROR';
        const message = error.response?.data?.error || this.$t(errorKey) || 'Failed to update selected FAQs.';
        useAlert(message, { type: 'error' });
      } finally {
        this.bulkFaqAction = '';
      }
    },

    async onFaqBulkDelete() {
      if (!this.currentAccountId || !this.selectedFaqIds.length) return;
      const confirmMessage = this.$t('BUSINESS_DATA.FAQS.BULK_DELETE_CONFIRM', { count: this.selectedFaqIds.length });
      if (!window.confirm(confirmMessage)) {
        return;
      }
      this.isBulkDeletingFaqs = true;
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/business_faqs/bulk_delete`, {
          ids: this.selectedFaqIds,
        });
        useAlert(
          this.$t('BUSINESS_DATA.FAQS.BULK_DELETE_SUCCESS', {
            count: this.selectedFaqIds.length,
          }),
          { type: 'success' }
        );
        this.onFaqClearSelection();
        await this.loadFaqs();
        this.updateSubTabCounts();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.FAQS.BULK_DELETE_ERROR') || 'Failed to delete selected FAQs.';
        useAlert(message, { type: 'error' });
      } finally {
        this.isBulkDeletingFaqs = false;
      }
    },

    async onFaqExport() {
      if (!this.currentAccountId) return;
      this.isExportingFaqsXlsx = true;
      try {
        const params = new URLSearchParams();
        if (this.faqFilters.search) params.append('search', this.faqFilters.search);
        if (this.faqFilters.category) params.append('category', this.faqFilters.category);
        if (this.faqFilters.status !== '') params.append('status', this.faqFilters.status);
        if (this.faqFilters.sort) params.append('sort', this.faqFilters.sort);
        const query = params.toString();
        const url = `/api/v1/accounts/${this.currentAccountId}/business_faqs/export.xlsx${query ? `?${query}` : ''}`;
        const response = await window.axios.get(url, { responseType: 'blob' });
        const contentType = response.headers['content-type'];
        const isXlsx = contentType && contentType.includes('spreadsheetml');
        const extension = isXlsx ? 'xlsx' : 'csv';
        const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.setAttribute('download', `business_faqs_${Date.now()}.${extension}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Failed to export business FAQs', error);
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.FAQS.EXPORT_ERROR') || 'Export failed.';
        useAlert(message, { type: 'error' });
      } finally {
        this.isExportingFaqsXlsx = false;
      }
    },

    async onFaqImport(file) {
      if (!this.currentAccountId || !file) return;
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!['csv', 'xlsx'].includes(extension || '')) {
        useAlert(
          this.$t('BUSINESS_DATA.FAQS.IMPORT_UNSUPPORTED') ||
            'Unsupported file type. Please upload CSV or XLSX.',
          { type: 'error' }
        );
        return;
      }
      this.isImportingFaqs = true;
      try {
        const form = new FormData();
        form.append('import_file', file);
        const { data } = await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/business_faqs/import`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        this.faqImportResults = data || {};
        const { created = 0, updated = 0, failed_count: failed = 0 } = this.faqImportResults;
        
        const message = this.$t('BUSINESS_DATA.FAQS.IMPORT_RESULT', { created, updated, failed });
        
        useAlert(message, {
          type: failed > 0 ? 'alert' : 'success',         
          persist: true,
          action: {
            type: 'button',
            message: this.$t('GENERAL.VIEW_DETAILS'),
            onClick: () => {
              this.$refs.faqsTab?.showImportResults();
            }
          }
        });
        await this.loadFaqs();
        this.updateSubTabCounts();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.FAQS.IMPORT_ERROR') || 'Import failed.';
        useAlert(message, { type: 'error' });
      } finally {
        this.isImportingFaqs = false;
      }
    },

    async onFaqToggleStatus(faq) {
      if (!this.currentAccountId || !faq) return;
      try {
        await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/business_faqs/${faq.id}`, {
          is_active: !faq.is_active,
        });
        const key = faq.is_active ? 'BUSINESS_DATA.FAQS.DEACTIVATED_SUCCESS' : 'BUSINESS_DATA.FAQS.ACTIVATED_SUCCESS';
        useAlert(this.$t(key), { type: 'success' });
        this.selectedFaqIds = this.selectedFaqIds.filter(id => id !== faq.id);
        await this.loadFaqs();
        this.updateSubTabCounts();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.FAQS.UPDATE_ERROR') || 'Failed to update FAQ.';
        useAlert(message, { type: 'error' });
      }
    },

    async deleteFaq(faq) {
      if (!this.currentAccountId || !faq) return;
      if (!window.confirm(this.$t('BUSINESS_DATA.FAQS.DELETE_CONFIRM'))) {
        return;
      }
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/business_faqs/${faq.id}`);
        useAlert(this.$t('BUSINESS_DATA.FAQS.DELETED_SUCCESS'), {type: 'success'});
        this.selectedFaqIds = this.selectedFaqIds.filter(id => id !== faq.id);
        await this.loadFaqs();
        this.updateSubTabCounts();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.FAQS.DELETE_ERROR') || 'Failed to delete FAQ.';
        useAlert(message, { type: 'error' });
      }
    },
   
    // Data Items methods
    onItemSearch(searchQuery) {
      this.itemFilters.search = searchQuery;
      this.itemPagination.current_page = 1;
      this.loadDataItems();
    },
    
    onItemFilterChange(filters) {
      this.itemFilters.category = filters.category;
      this.itemFilters.status = filters.status;
      this.itemFilters.web_content_source_id = filters.web_content_source_id;
      this.itemFilters.sort = filters.sort || 'created_at_desc';
      this.itemPagination.current_page = 1;
      this.loadDataItems();
    },
    
    onItemPageChange(page) {
      this.itemPagination.current_page = page;
      this.loadDataItems();
    },
    
    editDataItem(item) {
      this.editingItem = { ...item };
      this.showItemModal = true;
    },
    
    closeItemModal() {
      this.showItemModal = false;
      this.editingItem = null;
    },
    
    async onItemSaved() {
      const wasEditing = !!this.editingItem;
      this.closeItemModal();
      await this.loadDataItems();
      this.updateSubTabCounts();
      const message = wasEditing 
        ? this.$t('BUSINESS_DATA.ITEMS.UPDATED_SUCCESS')
        : this.$t('BUSINESS_DATA.ITEMS.ADDED_SUCCESS');
      useAlert(message, { type: 'success' });
    },

    onDataItemToggleSelect({ id, checked }) {
      if (!id) return;
      if (checked) {
        if (!this.selectedDataItemIds.includes(id)) {
          this.selectedDataItemIds = [...this.selectedDataItemIds, id];
        }
      } else {
        this.selectedDataItemIds = this.selectedDataItemIds.filter(existingId => existingId !== id);
      }
    },

    onDataItemToggleSelectPage({ ids, selectAll }) {
      const uniqueIds = Array.from(new Set(ids || []));
      if (!uniqueIds.length) {
        return;
      }
      if (selectAll) {
        const combined = new Set([...this.selectedDataItemIds, ...uniqueIds]);
        this.selectedDataItemIds = Array.from(combined);
      } else {
        this.selectedDataItemIds = this.selectedDataItemIds.filter(id => !uniqueIds.includes(id));
      }
    },

    onDataItemClearSelection() {
      this.selectedDataItemIds = [];
    },

    async onDataItemBulkUpdateStatus(isActive) {
      if (!this.currentAccountId || !this.selectedDataItemIds.length) return;
      this.bulkDataItemAction = isActive ? 'activate' : 'deactivate';
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/business_data_items/bulk_update_status`, {
          ids: this.selectedDataItemIds,
          is_active: isActive,
        });
        useAlert(
          this.$t('BUSINESS_DATA.GENERAL.BULK_UPDATE_SUCCESS', {
            count: this.selectedDataItemIds.length,
          }),
          { type: 'success' }
        );
        this.onDataItemClearSelection();
        await this.loadDataItems();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.ITEMS.BULK_UPDATE_ERROR') || this.$t('BUSINESS_DATA.ITEMS.UPDATE_ERROR') || 'Failed to update selected items.';
        useAlert(message, { type: 'error' });
      } finally {
        this.bulkDataItemAction = '';
      }
    },

    async onDataItemBulkDelete() {
      if (!this.currentAccountId || !this.selectedDataItemIds.length) return;
      const confirmMessage = this.$t('BUSINESS_DATA.GENERAL.BULK_DELETE_CONFIRM', { count: this.selectedDataItemIds.length });
      if (!window.confirm(confirmMessage)) {
        return;
      }
      this.isBulkDeletingDataItems = true;
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/business_data_items/bulk_delete`, {
          ids: this.selectedDataItemIds,
        });
        useAlert(
          this.$t('BUSINESS_DATA.GENERAL.BULK_DELETE_SUCCESS', {
            count: this.selectedDataItemIds.length,
          }),
          { type: 'success' }
        );
        this.onDataItemClearSelection();
        await this.loadDataItems();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.ITEMS.DELETE_ERROR') || 'Failed to delete selected items.';
        useAlert(message, { type: 'error' });
      } finally {
        this.isBulkDeletingDataItems = false;
      }
    },

    async onDataItemExport() {
      if (!this.currentAccountId) return;
      this.isExportingDataItemsXlsx = true;
      try {
        const params = new URLSearchParams();
        if (this.itemFilters.search) params.append('search', this.itemFilters.search);
        if (this.itemFilters.category) params.append('category', this.itemFilters.category);
        if (this.itemFilters.status !== '') params.append('status', this.itemFilters.status);
        if (this.itemFilters.web_content_source_id) params.append('web_content_source_id', this.itemFilters.web_content_source_id);
        if (this.itemFilters.sort) params.append('sort', this.itemFilters.sort);
        const query = params.toString();
        const url = `/api/v1/accounts/${this.currentAccountId}/business_data_items/export.xlsx${query ? `?${query}` : ''}`;
        const response = await window.axios.get(url, { responseType: 'blob' });
        const contentType = response.headers['content-type'];
        const isXlsx = contentType && contentType.includes('spreadsheetml');
        const extension = isXlsx ? 'xlsx' : 'csv';
        const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.setAttribute('download', `business_data_items_${Date.now()}.${extension}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Failed to export business data items', error);
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.ITEMS.EXPORT_ERROR') || 'Export failed.';
        useAlert(message, { type: 'error' });
      } finally {
        this.isExportingDataItemsXlsx = false;
      }
    },

    async onDataItemImport(file) {
      if (!this.currentAccountId || !file) return;
      this.isImportingDataItems = true;
      try {
        const form = new FormData();
        form.append('import_file', file);
        const { data } = await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/business_data_items/import`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        this.dataItemImportResults = data || {};
        const { created = 0, updated = 0, failed_count: failed = 0 } = this.dataItemImportResults;
        const message = this.$t('BUSINESS_DATA.ITEMS.IMPORT_RESULT', { created, updated, failed }) ||
                        `Imported: ${created}, Updated: ${updated}, Failed: ${failed}`;
        useAlert(message, {
          type: failed > 0 ? 'alert' : 'success',
          persist: true,
          action: {
            type: 'button',
            message: this.$t('GENERAL.VIEW_DETAILS'),
            onClick: () => {
              this.$refs.dataItemsTab?.showImportResults();
            },
          },
        });
        await this.loadDataItems();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.ITEMS.IMPORT_ERROR') || 'Import failed.';
        useAlert(message, { type: 'error' });
      } finally {
        this.isImportingDataItems = false;
      }
    },

    async onDataItemToggleStatus(item) {
      if (!this.currentAccountId || !item) return;
      try {
        await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/business_data_items/${item.id}`, {
          is_active: !item.is_active,
        });
        const messageKey = item.is_active ? 'BUSINESS_DATA.ITEMS.DEACTIVATED_SUCCESS' : 'BUSINESS_DATA.ITEMS.ACTIVATED_SUCCESS';
        useAlert(this.$t(messageKey), { type: 'success' });
        await this.loadDataItems();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.ITEMS.UPDATE_ERROR') || 'Failed to update item.';
        useAlert(message, { type: 'error' });
      }
    },

    async deleteDataItem(item) {
      if (!this.currentAccountId || !item) return;
      if (!window.confirm(this.$t('BUSINESS_DATA.ITEMS.DELETE_CONFIRM'))) {
        return;
      }
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/business_data_items/${item.id}`);
        useAlert(this.$t('BUSINESS_DATA.ITEMS.DELETED_SUCCESS'), {
          type: 'success',
        });
        this.onDataItemClearSelection();
        await this.loadDataItems();
      } catch (error) {
        const message = error.response?.data?.error || this.$t('BUSINESS_DATA.ITEMS.DELETE_ERROR') || 'Failed to delete item.';
        useAlert(message, { type: 'error' });
      }
    },
    
    // Web Sources methods
    onSourceSearch(searchQuery) {
      this.sourceFilters.search = searchQuery;
      this.sourcePagination.current_page = 1;
      this.loadWebContentSources();
    },
    
    onSourceFilterChange(filters) {
      this.sourceFilters.status = filters.status;
      this.sourceFilters.type = filters.type;
      this.sourcePagination.current_page = 1;
      this.loadWebContentSources();
    },
    
    onSourcePageChange(page) {
      this.sourcePagination.current_page = page;
      this.loadWebContentSources();
    },
    
    editSource(source) {
      this.editingSource = source;
      this.showSourceModal = true;
    },
    
    closeSourceModal() {
      this.showSourceModal = false;
      this.editingSource = null;
    },
    
    async onSourceSaved() {
      const wasEditing = !!this.editingSource;
      this.closeSourceModal();
      await this.loadWebContentSources();
      this.updateSubTabCounts();
      const message = wasEditing 
        ? this.$t('WEB_CONTENT.SOURCES.UPDATED_SUCCESS')
        : this.$t('WEB_CONTENT.SOURCES.ADDED_SUCCESS');
      useAlert(message, { type: 'success' });
    },
    
    onCategoryCreated(newCategory) {
      if (newCategory.type === 'faq') {
        this.businessFaqCategories.push(newCategory);
      } else if (newCategory.type === 'data') {
        this.businessDataCategories.push(newCategory);
      }
    },

    // AI Rules methods
    openAiRuleModal() {
      this.editingAiRule = null;
      this.showAiRuleModal = true;
    },

    editAiRule(rule) {
      this.editingAiRule = { ...rule };
      this.showAiRuleModal = true;
    },

    closeAiRuleModal() {
      this.showAiRuleModal = false;
      this.editingAiRule = null;
    },

    async onAiRuleSave(form) {
      if (!this.currentAccountId) return;
      this.isSavingAiRule = true;
      try {
        if (this.editingAiRule) {
          await window.axios.put(
            `/api/v1/accounts/${this.currentAccountId}/ai_rules/${this.editingAiRule.id}`,
            { ai_rule: form }
          );
          useAlert(this.$t('BUSINESS_DATA.AI_RULES.UPDATED_SUCCESS'), {
            type: 'success',
          });
        } else {
          await window.axios.post(
            `/api/v1/accounts/${this.currentAccountId}/ai_rules`,
            { ai_rule: form }
          );
          useAlert(this.$t('BUSINESS_DATA.AI_RULES.CREATED_SUCCESS'), {
            type: 'success',
          });
        }
        this.closeAiRuleModal();
        await this.loadAiRules();
      } catch (error) {
        console.error('Failed to save AI rule:', error);
        const message = error.response?.data?.errors?.join(', ') || this.$t('BUSINESS_DATA.AI_RULES.SAVE_FAILED');
        useAlert(message, { type: 'error' });
      } finally {
        this.isSavingAiRule = false;
      }
    },

    async deleteAiRule(rule) {
      if (!this.currentAccountId || !rule) return;
      const confirmed = window.confirm(this.$t('BUSINESS_DATA.AI_RULES.DELETE_CONFIRM'));
      if (!confirmed) return;
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/ai_rules/${rule.id}`);
        useAlert(this.$t('BUSINESS_DATA.AI_RULES.DELETED_SUCCESS'), {
          type: 'success',
        });
        await this.loadAiRules();
      } catch (error) {
        console.error('Failed to delete AI rule:', error);
        useAlert(this.$t('BUSINESS_DATA.AI_RULES.DELETE_FAILED'), {
          type: 'error',
        });
      }
    },

    onAiRuleSearch(term) {
      this.aiRuleFilters.search = term;
      this.aiRulePagination.current_page = 1;
      this.isSearchingAiRules = true;
      this.loadAiRules();
    },

    onAiRulePageChange(page) {
      this.aiRulePagination.current_page = page;
      this.loadAiRules();
    },

    // AI Sample Sentences methods
    openAiSampleModal() {
      this.editingAiSampleSentence = null;
      this.showAiSampleModal = true;
    },

    editAiSampleSentence(sample) {
      this.editingAiSampleSentence = { ...sample };
      this.showAiSampleModal = true;
    },

    closeAiSampleModal() {
      this.showAiSampleModal = false;
      this.editingAiSampleSentence = null;
    },

    async onAiSampleSave(form) {
      if (!this.currentAccountId) return;
      this.isSavingAiSample = true;
      try {
        if (this.editingAiSampleSentence) {
          await window.axios.put(
            `/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences/${this.editingAiSampleSentence.id}`,
            { ai_sample_sentence: form }
          );
          useAlert(this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.UPDATED_SUCCESS'), {
            type: 'success',
          });
        } else {
          await window.axios.post(
            `/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences`,
            { ai_sample_sentence: form }
          );
          useAlert(this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.CREATED_SUCCESS'), {
            type: 'success',
          });
        }
        this.closeAiSampleModal();
        await this.loadAiSampleSentences();
      } catch (error) {
        console.error('Failed to save AI sample sentence:', error);
        const message = error.response?.data?.errors?.join(', ') || this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.SAVE_FAILED');
        useAlert(message, { type: 'error' });
      } finally {
        this.isSavingAiSample = false;
      }
    },

    async deleteAiSampleSentence(sample) {
      if (!this.currentAccountId || !sample) return;
      const confirmed = window.confirm(this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.DELETE_CONFIRM'));
      if (!confirmed) return;
      try {
        await window.axios.delete(
          `/api/v1/accounts/${this.currentAccountId}/ai_sample_sentences/${sample.id}`
        );
        useAlert(this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.DELETED_SUCCESS'), {
          type: 'success',
        });
        await this.loadAiSampleSentences();
      } catch (error) {
        console.error('Failed to delete AI sample sentence:', error);
        useAlert(this.$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.DELETE_FAILED'), {
          type: 'error',
        });
      }
    },

    onAiSampleSearch(term) {
      this.aiSampleFilters.search = term;
      this.aiSamplePagination.current_page = 1;
      this.isSearchingAiSamples = true;
      this.loadAiSampleSentences();
    },

    onAiSampleSortChange(sort) {
      this.aiSampleFilters.sort = sort || 'created_at_desc';
      this.aiSamplePagination.current_page = 1;
      this.loadAiSampleSentences();
    },

    onAiSamplePageChange(page) {
      this.aiSamplePagination.current_page = page;
      this.loadAiSampleSentences();
    },

    onCategoryUpdated() {
      this.loadBusinessCategories();
    },

    updateSubTabCounts() {
      // Placeholder for future enhancements; currently counts handled at API level.
    },
  },
};
</script>
